/**
 * Estate Flow - System Health & Diagnostics Panel
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  RefreshCw,
  Database,
  CreditCard,
  Webhook,
  Server,
  Settings,
  Loader2,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SystemHealthPanel = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const fetchSystemHealth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${API}/admin/system-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHealthData(response.data);
      setLastChecked(new Date());
      
      // Show toast if critical issues found
      if (response.data.overall_status === 'critical') {
        toast.error('System health check found critical issues!', {
          description: `${response.data.summary.errors} component(s) failing`
        });
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error('Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'error':
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
        return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800">Warning</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Error</Badge>;
      case 'critical':
        return <Badge className="bg-red-600 text-white">Critical</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const getComponentIcon = (component) => {
    switch (component) {
      case 'database':
        return <Database className="h-4 w-4" />;
      case 'stripe':
      case 'plugnpay':
        return <CreditCard className="h-4 w-4" />;
      case 'webhook_production':
        return <Webhook className="h-4 w-4" />;
      case 'backend':
        return <Server className="h-4 w-4" />;
      case 'environment':
        return <Settings className="h-4 w-4" />;
      case 'email_smtp':
        return <Mail className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getComponentTitle = (component) => {
    const titles = {
      'database': 'MongoDB Database',
      'stripe': 'Stripe Payment Gateway',
      'plugnpay': 'PlugnPay Payment Gateway',
      'webhook_production': 'Production Webhook Endpoint',
      'backend': 'Backend Service',
      'environment': 'Environment Variables',
      'email_smtp': 'Email SMTP Service'
    };
    return titles[component] || component;
  };

  if (!healthData && loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            System Health & Diagnostics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-600">Running diagnostics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              System Health & Diagnostics
            </CardTitle>
            <CardDescription className="mt-2">
              Real-time monitoring of all Estate Flow system components
            </CardDescription>
          </div>
          <Button 
            onClick={fetchSystemHealth} 
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {healthData && (
          <div className="space-y-6">
            {/* Overall Status */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold flex items-center">
                    {getStatusIcon(healthData.overall_status)}
                    <span className="ml-2">Overall System Status</span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Last checked: {lastChecked ? lastChecked.toLocaleString() : 'Never'}
                  </p>
                </div>
                {getStatusBadge(healthData.overall_status)}
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-2xl font-bold text-green-700">
                        {healthData.summary.healthy}
                      </p>
                      <p className="text-sm text-green-600">Healthy</p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-2xl font-bold text-yellow-700">
                        {healthData.summary.warnings}
                      </p>
                      <p className="text-sm text-yellow-600">Warnings</p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                    <div>
                      <p className="text-2xl font-bold text-red-700">
                        {healthData.summary.errors}
                      </p>
                      <p className="text-sm text-red-600">Errors</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual Component Checks */}
            <div>
              <h3 className="text-md font-semibold mb-4">Component Status</h3>
              <div className="space-y-3">
                {Object.entries(healthData.checks).map(([component, check]) => (
                  <div 
                    key={component}
                    className={`border rounded-lg p-4 ${
                      check.status === 'error' ? 'border-red-300 bg-red-50' :
                      check.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                      'border-green-300 bg-green-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          {getComponentIcon(component)}
                          <h4 className="font-semibold ml-2">{getComponentTitle(component)}</h4>
                          <div className="ml-3">
                            {getStatusBadge(check.status)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{check.message}</p>
                        
                        {/* Details */}
                        {check.details && (
                          <div className="mt-3 bg-white bg-opacity-50 p-3 rounded text-xs font-mono">
                            {Object.entries(check.details).map(([key, value]) => (
                              <div key={key} className="flex justify-between py-1">
                                <span className="text-gray-600">{key}:</span>
                                <span className="text-gray-900 font-medium">
                                  {typeof value === 'boolean' ? (value ? '✓' : '✗') : 
                                   Array.isArray(value) ? value.join(', ') :
                                   String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Critical Webhook Error Callout */}
                        {component === 'webhook_production' && check.status === 'error' && check.details?.status_code === 404 && (
                          <div className="mt-3 bg-red-100 border border-red-300 p-3 rounded">
                            <p className="text-sm font-semibold text-red-800 mb-1">
                              🚨 CRITICAL: Webhook Routing Issue
                            </p>
                            <p className="text-xs text-red-700">
                              Your custom domain is not routing /api/* requests to the backend. 
                              This will prevent Stripe recurring billing from working.
                            </p>
                            <p className="text-xs text-red-700 mt-2">
                              <strong>Action Required:</strong> Contact Emergent support to fix /api/* routing to backend (port 8001).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {(healthData.summary.errors > 0 || healthData.summary.warnings > 0) && (
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold mb-3">Recommended Actions</h3>
                <ul className="space-y-2">
                  {healthData.summary.errors > 0 && (
                    <li className="flex items-start text-sm">
                      <XCircle className="h-4 w-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong className="text-red-700">Critical Issues Found:</strong> Review errors above and contact support if needed.
                      </span>
                    </li>
                  )}
                  {healthData.checks.webhook_production?.status === 'error' && (
                    <li className="flex items-start text-sm">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong className="text-yellow-700">Webhook Routing:</strong> Contact Emergent support to enable /api/* routing on your custom domain.
                      </span>
                    </li>
                  )}
                  {healthData.summary.warnings > 0 && (
                    <li className="flex items-start text-sm">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong className="text-yellow-700">Configuration Warnings:</strong> Review warnings to ensure optimal system performance.
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthPanel;
